import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sites, hotTags } from '@/mocks/sites';
import SearchBox from '@/components/feature/SearchBox';
import SiteCard from '@/components/feature/SiteCard';
import FilterPanel from '@/components/feature/FilterPanel';
import Pagination from '@/components/base/Pagination';
import {
  defaultFilters,
  filterSites,
  getRegions,
  getScales,
  searchSites,
  sortSites,
  sortOptions,
  type SiteFilterState,
} from '@/utils/siteUtils';

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';

  const [filters, setFilters] = useState<SiteFilterState>({ ...defaultFilters });
  const [sort, setSort] = useState<string>('hot');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const matched = useMemo(() => searchSites(sites, keyword), [keyword]);
  const regions = useMemo(() => getRegions(matched), [matched]);
  const scales = useMemo(() => getScales(matched), [matched]);
  const result = useMemo(() => sortSites(filterSites(matched, filters), sort), [matched, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const paged = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setFilters({ ...defaultFilters });
    setSort('hot');
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  const handleFilterChange = (next: Partial<SiteFilterState>) => setFilters((prev) => ({ ...prev, ...next }));
  const handleReset = () => setFilters({ ...defaultFilters });

  const hotSites = sortSites(sites, 'hot').slice(0, 6);

  return (
    <div className="w-full animate-fade-in px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-[1280px]">
        <header className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">搜索网站</h1>
          <p className="mt-1 text-sm text-foreground-500">
            {keyword ? (
              <>
                关键词：<strong className="text-primary-600">{keyword}</strong>
              </>
            ) : (
              '输入关键词，快速找到你需要的优质网站'
            )}
          </p>
          <div className="mt-4 max-w-2xl">
            <SearchBox key={keyword} initialValue={keyword} placeholder="输入关键词，例如“在线工具”" />
          </div>
        </header>

        {!keyword && (
          <section className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 text-base font-semibold text-foreground-900">开始你的搜索</h2>
            <p className="mt-1.5 text-sm text-foreground-500">试试下面这些热门关键词，或直接在上方输入框搜索。</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {hotTags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </section>
        )}

        {keyword && (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-4">
                <FilterPanel
                  filters={filters}
                  regions={regions}
                  scales={scales}
                  onChange={handleFilterChange}
                  onReset={handleReset}
                />
              </div>
            </aside>

            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-600 lg:hidden"
                  >
                    <i className="ri-filter-3-line"></i>
                    筛选
                  </button>
                  <span className="text-sm text-foreground-500">
                    共找到 <strong className="text-foreground-900">{result.length}</strong> 个结果
                  </span>
                </div>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSort(option.value)}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        sort === option.value
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-primary-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {paged.length > 0 ? (
                <>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {paged.map((site) => (
                      <SiteCard key={site.id} site={site} compact />
                    ))}
                  </div>
                  <Pagination page={page} totalPages={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </span>
                  <h2 className="mt-4 text-base font-semibold text-foreground-900">没有找到相关网站</h2>
                  <p className="mt-1.5 text-sm text-foreground-500">
                    换个关键词试试，或直接浏览下方的热门网站入口。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    <Link
                      to="/search"
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-eraser-line"></i>
                      清空搜索
                    </Link>
                    <Link
                      to="/category/all"
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      浏览全部网站
                    </Link>
                  </div>

                  <div className="mt-10 text-left">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                      <i className="ri-fire-line text-accent-500"></i>
                      热门网站推荐
                    </h3>
                    <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {hotSites.map((site) => (
                        <SiteCard key={site.id} site={site} compact />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[80%] overflow-y-auto rounded-t-lg bg-background-50 p-5">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-950">筛选条件</span>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              regions={regions}
              scales={scales}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50"
            >
              查看结果（{result.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}